import * as Papa from 'papaparse';
import { noop } from 'vet-bones/utils';

import { formatSeparator } from 'src/utils/csv/formatSeparator';

type CsvField<T extends Record<string, any>> = {
  label: string;
  value: string;
  statistics: T;
};

export type ExtendedParseError = Partial<Papa.ParseError> &
  Pick<Papa.ParseError, 'message' | 'row'>;

export type ParseCsvResult<T extends Record<string, any>> = {
  data?: {
    rows: string[][];
    fields: Array<CsvField<T>>;
  };
  errors?: ExtendedParseError[];
};

export const parseCsv = <T extends Record<string, any>>(
  file: string | File,
  options: Partial<Papa.ParseConfig>,
  defaultStatistics: T,
  assignStatistics?: (fields: Array<CsvField<T>>, rows: string[][]) => void,
  limitRows?: number
) => {
  let abort: () => void = noop;

  const fields: Array<CsvField<T>> = [];
  const rows: string[][] = [];
  const errors: ExtendedParseError[] = [];

  const parsePromise = new Promise<ParseCsvResult<T>>((resolve) => {
    let parser: Papa.Parser | null = null;
    abort = () => {
      if (parser) {
        parser.abort();
        parser = null;
        resolve({ errors });
      }
    };
    const complete = () => {
      if (assignStatistics) {
        assignStatistics(fields, rows);
      }

      parser = null;
      resolve({ data: { fields, rows } });
    };

    let lastPause = Date.now();
    const handleStep: Papa.ParseConfig['step'] = async (result, stepParser) => {
      if (result.meta.aborted) {
        return;
      }

      parser = stepParser;

      const now = Date.now();
      if (now - lastPause > 200) {
        lastPause = now;

        parser.pause();
        setTimeout(() => {
          parser?.resume();
        }, 0);
      }

      if (result.errors.length) {
        errors.push(...result.errors);
        abort();
        return;
      }

      if (!fields.length) {
        if (result.meta.fields) {
          if (result.meta.fields.some((field) => !field)) {
            const headerPreview = result.meta.fields.join(',');
            errors.push({
              message: `Error importing: A header name is missing in [${headerPreview}]`,
              row: 0,
            });
            abort();
            return;
          }
          fields.push(
            ...result.meta.fields.map<CsvField<T>>((field) => ({
              label: field,
              value: field,
              statistics: { ...defaultStatistics },
            }))
          );
        } else {
          fields.push(
            ...new Array(result.data.length || 0)
              .fill('')
              .map<CsvField<T>>((_, idx) => ({
                label: `Field ${idx + 1}`,
                value: `${idx}`,
                statistics: { ...defaultStatistics },
              }))
          );
        }
      }

      if (limitRows != null && rows.length >= limitRows) {
        parser.abort();
        complete();
        return;
      }

      const row = Array.isArray(result.data)
        ? result.data
        : fields.map((field) => (result.data as any)[field.value]);
      rows.push(row);
    };

    const handleComplete: Papa.ParseConfig['complete'] = (result) => {
      if (result.meta.aborted) {
        return;
      }

      complete();
    };

    // papaparse can actually take File, I think they removed the
    // File type because nodejs can't handle File
    Papa.parse(file as any, {
      ...options,
      delimiter:
        typeof options.delimiter === 'string'
          ? formatSeparator(options.delimiter)
          : options.delimiter,
      skipEmptyLines: true,
      step: handleStep,
      complete: handleComplete,
    });
  });

  return {
    abort: () => {
      errors.length = 0;
      abort();
    },
    parsePromise,
  };
};
